<template>
  <div class="page-container">
    <div class="page-title-container d-flex justify-content-between align-items-center">
      <h1 class="page-title mb-0">Historial de lavados</h1>
      <div>
        <b-button variant="none" class="button button-light mr-2" :to="{ name: 'fleet-washes' }">Volver</b-button>
        <b-button variant="none" class="button button-primary" :to="{ name: 'fleet-wash-create' }">
          <i class="ri-add-line mr-1"></i> Registrar lavado
        </b-button>
      </div>
    </div>

    <div class="table-container">
      <div class="table-header">
        <div class="w-full d-flex flex-wrap align-items-end">
          <div class="filter-field mr-2 mb-2">
            <label class="filter-label">Desde</label>
            <input type="date" v-model="filters.from" class="toolbar-select" />
          </div>
          <div class="filter-field mr-2 mb-2">
            <label class="filter-label">Hasta</label>
            <input type="date" v-model="filters.to" class="toolbar-select" />
          </div>
          <div class="filter-field mr-2 mb-2">
            <label class="filter-label">Vehículo</label>
            <select v-model="filters.vehicleId" class="toolbar-select">
              <option :value="null">Todos</option>
              <option v-for="v in vehicles" :key="v.id" :value="v.id">{{ v.license_plate }}</option>
            </select>
          </div>
          <div class="filter-field mr-2 mb-2">
            <label class="filter-label">Tipo</label>
            <select v-model="filters.washType" class="toolbar-select">
              <option :value="null">Todos</option>
              <option v-for="t in washTypes" :key="t.key" :value="t.key">{{ t.label }}</option>
            </select>
          </div>
          <div class="mb-2">
            <b-button variant="none" class="button button-primary mr-2" @click="applyFilters">Filtrar</b-button>
            <b-button variant="none" class="button button-light" @click="clearFilters">Limpiar</b-button>
          </div>
        </div>
      </div>

      <div class="table-responsive mb-0 table-min-height">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th class="pl-3">Fecha</th>
              <th>Vehículo</th>
              <th>Tipo</th>
              <th>Car wash</th>
              <th>Costo</th>
              <th>Conductor</th>
              <th>Foto</th>
              <th class="pr-md-4">Opciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in list" :key="o.id">
              <td class="text-center pl-3">{{ formatDate(o.wash_date) }}</td>
              <td class="text-center">{{ o.vehicle_plate }}</td>
              <td class="text-center">{{ washTypeLabel(o.wash_type) }}</td>
              <td class="text-center">{{ o.car_wash_name || '—' }}</td>
              <td class="text-center">{{ o.cost != null ? formatMoney(o.cost) : '—' }}</td>
              <td class="text-center">{{ o.driver_name || '—' }}</td>
              <td class="text-center">
                <button v-if="o.has_photo" type="button" class="photo-link" title="Ver foto"
                  @click="openPhoto(o)"><i class="ri-image-line"></i></button>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-center d_option pr-md-3">
                <DropdownTable>
                  <template slot="dropdown-content">
                    <DropdownItem text="Editar"
                      @click="$router.push({ name: 'fleet-wash-edit', params: { id: o.id } })" />
                    <DropdownItem text="Eliminar" @click="openDelete(o)" />
                  </template>
                </DropdownTable>
              </td>
            </tr>
            <tr v-if="!list.length">
              <td colspan="8" class="text-center text-muted py-4">No se encontraron lavados.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="table-footer d-flex justify-content-end align-items-center">
        <pagination :total="total" :page="page" :page-size="pageSize" :pages="pages"
          :changePage="changePage" :changePageSize="changePageSize" />
      </div>
    </div>

    <b-modal v-model="deleteModal.show" title="Eliminar lavado" hide-footer>
      <p>¿Está seguro que desea eliminar este lavado? Esta acción no se puede deshacer.</p>
      <div class="text-right">
        <b-button variant="none" class="button button-light mr-2" @click="deleteModal.show = false">Cancelar</b-button>
        <b-button variant="none" class="button button-primary" @click="confirmDelete">Eliminar</b-button>
      </div>
    </b-modal>

    <b-modal v-model="photoModal.show" size="lg" hide-footer title="Foto del lavado">
      <div class="photo-frame text-center">
        <img v-if="photoModal.url" :src="photoModal.url" alt="Foto del lavado" class="photo-img" />
        <p v-else-if="photoModal.error" class="text-muted m-0 py-4">{{ photoModal.error }}</p>
        <p v-else class="text-muted m-0 py-4">Cargando imagen…</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Alert, PaginationMixin, DropdownTable, DropdownItem } from 'wize-admin'
import { FleetWashService, VehicleService } from '@/core/services'

const WASH_TYPES = [
  { key: 'BASIC', label: 'Básico' },
  { key: 'FULL', label: 'Completo' },
  { key: 'INTERIOR_ONLY', label: 'Solo interior' }
]

export default {
  name: 'WashList',
  components: { DropdownTable, DropdownItem },
  mixins: [PaginationMixin],
  data() {
    return {
      list: [],
      vehicles: [],
      washTypes: WASH_TYPES,
      filters: { from: '', to: '', vehicleId: null, washType: null },
      deleteModal: { show: false, id: null },
      photoModal: { show: false, url: null, error: null }
    }
  },
  methods: {
    washTypeLabel(t) { return (WASH_TYPES.find((x) => x.key === t) || {}).label || t },
    formatDate(value) {
      if (!value) return '—'
      const parts = String(value).slice(0, 10).split('-')
      if (parts.length !== 3) return value
      return `${parts[2]}/${parts[1]}/${parts[0]}`
    },
    formatMoney(v) {
      return `S/ ${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    buildParams() {
      const f = this.filters
      const p = {}
      if (f.from) p.from = f.from
      if (f.to) p.to = f.to
      if (f.vehicleId) p.vehicle_id = f.vehicleId
      if (f.washType) p.wash_type = f.washType
      return p
    },
    applyFilters() {
      this.page = 1
      this.loadData()
    },
    clearFilters() {
      this.filters = { from: '', to: '', vehicleId: null, washType: null }
      this.applyFilters()
    },
    async loadVehicles() {
      try {
        const res = await VehicleService.list({ is_travelcab: true }, { page: 0, size: 1000 })
        const payload = res.payload || {}
        this.vehicles = payload.items || payload || []
      } catch (e) { console.error(e) }
    },
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const params = { ...this.buildParams(), page: this.page, limit: this.pageSize }
        const res = await FleetWashService.list(params)
        this.list = res.payload || []
        this.total = res.metadata ? res.metadata.totalElements : this.list.length
        this.pages = res.metadata ? res.metadata.totalPages : 1
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al listar los lavados')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    openDelete(o) { this.deleteModal = { show: true, id: o.id } },
    async confirmDelete() {
      try {
        await this.$store.dispatch('app/loading', true)
        await FleetWashService.remove(this.deleteModal.id)
        this.deleteModal.show = false
        Alert.success('Lavado eliminado correctamente')
        await this.loadData()
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'No se pudo eliminar el lavado')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async openPhoto(o) {
      this.photoModal = { show: true, url: null, error: null }
      try {
        const res = await FleetWashService.getPhoto(o.id)
        this.photoModal.url = res.payload ? res.payload.url : null
        if (!this.photoModal.url) this.photoModal.error = 'No hay foto disponible.'
      } catch (e) {
        console.error(e)
        this.photoModal.error = e.errors ? e.errors.message : 'No se pudo cargar la foto.'
      }
    }
  },
  async mounted() {
    // Prefiltro por vehículo si viene en la query (desde el dashboard).
    if (this.$route.query.vehicle_id) {
      this.filters.vehicleId = Number(this.$route.query.vehicle_id)
    }
    await this.loadVehicles()
    await this.loadData()
  }
}
</script>

<style lang="stylus" scoped>
.filter-field
  display flex
  flex-direction column
.filter-label
  font-size 11px
  color #6c757d
  margin-bottom 2px
.photo-link
  background none
  border none
  color #007bff
  font-size 16px
  cursor pointer
  &:hover
    color #0056b3
.photo-frame
  background #f4f5f7
  border 1px solid #e5e7eb
  border-radius 8px
  min-height 220px
  display flex
  align-items center
  justify-content center
  overflow hidden
.photo-img
  max-width 100%
  max-height 70vh
  object-fit contain
</style>
